import { Component, ElementRef, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { environment } from '@environments/environment';
import { Sliderimage } from '@model/sliderimage';
import { AnalyticsService } from '@service/analytics.service';
import { CacheService } from '@service/cache.service';
import { MobileService } from '@service/mobile.service';
import { SliderImagesService } from '@service/slider-images.service';
import {
  EMPTY,
  OperatorFunction,
  Subject,
  combineLatest,
  connect,
  distinctUntilChanged,
  filter,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { SharedModule } from 'src/app/shared.module';
import { ImageSliderBarComponentModule } from '../image-slider-bar/image-slider-bar.module';

@Component({
  selector: 'image-slider',
  templateUrl: './image-slider.component.html',
  styleUrls: ['./image-slider.component.scss'],
  host: {
    '[style.padding-bottom]': `sliderItems?.length > 0 ? '0px' : no_image_margin`,
  },
})
export class ImageSliderComponent implements OnInit, OnDestroy {
  public environment = environment;

  @Input()
  public page: String = null;

  @Input()
  public hasImageSliderBar: boolean = false;

  @Input('no-image-margin')
  public no_image_margin: string = '0px';

  public selected_index = null;

  public interval = 15_000;

  public sliderItems: Sliderimage[] = null;

  private imageObservable = new Subject<number>();
  private visibleObservable = new Subject<boolean>();
  private destroyObservable = new Subject<void>();

  private timeout: ReturnType<typeof setTimeout> | undefined = undefined;

  constructor(
    private elementRef: ElementRef,
    private sliderImagesService: SliderImagesService,
    private CacheService: CacheService,
    public MobileService: MobileService,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit() {
    const cacheResult = this.CacheService.getByIndex(this.page);

    if (cacheResult) {
      this.sliderItems = cacheResult.images;
      this.sliderImagesService.hasSliderImages = cacheResult.hasSliderImages;
      this.scheduleNext();

      if (this.sliderItems.length > 0) {
        this.imageObservable.next(0);
      }
    } else {
      this.sliderImagesService.getByPage(this.page).then((response) => {
        this.sliderItems = response.images;
        this.sliderImagesService.hasSliderImages = response.hasSliderImages;
        this.CacheService.setIndex(this.page, response);

        this.scheduleNext();

        if (this.sliderItems.length > 0) {
          this.imageObservable.next(0);
        }
      });
    }

    // Start tracking this component for visibility changes.
    this.analyticsService.trackElementVisibility(this.elementRef.nativeElement, (state) => {
      this.visibleObservable.next(state);
    });

    combineLatest({
      visible: this.visibleObservable.pipe(distinctUntilChanged()),
      idx: this.imageObservable.pipe(distinctUntilChanged()),
    })
      .pipe(
        takeUntil(this.destroyObservable),
        trackChangesWithDuration(),

        // Ignore short events like the user quickly scrolling over a
        // slider so we don't flood the database.
        filter((event) => event.duration >= 250),
      )
      .subscribe((result) => {
        const idxChanged = result.newValue.idx != result.lastValue.idx;

        // Visibility changed from true to false, but the image shown
        // did not => The user scrolled away from the slider.
        const wentInvisible = !idxChanged && result.lastValue.visible && !result.newValue.visible;

        // The displayed image changed, but the slider remained visible
        // => The user did not scroll away, but the slider
        // automatically cycled the image.
        const visibleImageChanged = idxChanged && result.lastValue.visible && result.newValue.visible;

        if (wentInvisible || visibleImageChanged) {
          const idx = result.lastValue.idx;
          const image = this.sliderItems[idx];

          this.analyticsService.addEvent({
            type: 'view',
            target_id: image.id,
            duration: result.duration,
          });
        }
      });
  }

  ngOnDestroy() {
    this.visibleObservable.next(false);
    this.analyticsService.stopTracking(this.elementRef.nativeElement);
    this.destroyObservable.next(undefined);
    this.destroyObservable.complete();
  }

  public addClickEvent(id: number) {
    this.analyticsService.addEvent({
      type: 'click',
      target_id: id,
      duration: 0,
    });
  }

  public next() {
    if (this.imageCount === 0) {
      return;
    }

    this.scheduleNext();

    const isLast = this.selected_index >= this.sliderItems.length - 1;

    this.selected_index = isLast ? 0 : this.selected_index + 1;

    this.imageObservable.next(this.selected_index);
  }

  public previous() {
    if (this.imageCount === 0) {
      return;
    }

    this.scheduleNext();

    const isFirst = this.selected_index <= 0;

    this.selected_index = isFirst ? this.sliderItems.length - 1 : this.selected_index - 1;

    this.imageObservable.next(this.selected_index);
  }

  private scheduleNext() {
    this.clearTimeout();

    this.timeout = setTimeout(() => {
      this.timeout = undefined;
      this.next();
    }, this.interval);
  }

  private clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  }

  public get current() {
    if (!this.sliderItems) {
      return undefined;
    }

    if (
      this.selected_index != null &&
      this.selected_index > -1 &&
      this.selected_index < this.sliderItems.length &&
      typeof this.sliderItems[this.selected_index] != 'undefined'
    ) {
      return this.sliderItems[this.selected_index];
    } else {
      return this.sliderItems[0];
    }
  }

  public get getHasImageSliderBar() {
    return this.hasImageSliderBar;
  }

  private get imageCount() {
    return this.sliderItems?.length ?? 0;
  }
}

@NgModule({
  declarations: [ImageSliderComponent],
  imports: [SharedModule, ImageSliderBarComponentModule],
  exports: [ImageSliderComponent, SharedModule],
})
export class ImageSliderComponentModule {}

function trackChangesWithDuration<T>(): OperatorFunction<T, { duration: number; lastValue: T; newValue: T }> {
  return connect((source) => {
    let lastValue = undefined;
    let lastTime = undefined;

    return source.pipe(
      switchMap((value) => {
        if (lastTime === undefined) {
          lastValue = value;
          lastTime = Date.now();
          return EMPTY;
        }

        const now = Date.now();
        const diff = now - lastTime;
        const lastValueCopy = lastValue;

        lastTime = now;
        lastValue = value;

        return of({
          duration: diff,
          lastValue: lastValueCopy,
          newValue: value,
        });
      }),
    );
  });
}
